import React from "react"
import { BLOCKS, MARKS, INLINES } from "@contentful/rich-text-types"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import "./RichText.scss"
import formatTheme from "../../utils/organism-formatting/formatTheme"
import PageSections from "../PageSections/PageSections"
import GeneralPageSections from "../PageSections/GeneralPageSections"
import FormatAndRenderLayoutTestimonials from "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutTestimonials"
import FormatAndRenderActionBlock from "../../utils/organism-formatting/ActionBlock/FormatAndRenderActionBlock"
import { Link } from "gatsby"
import formatIcons from "../../utils/organism-formatting/formatIcons"
import { FocusedImage, Icon } from "terra-one"
import { VideoCovered } from "trimble-terra"

const RichText = ({
  contentfulId,
  richText,
  isChildContent,
  parentColorScheme,
  colorScheme,
  nested,
  proseText,
  assetHyperLink,
  includeClasses = true,
}) => {
  const bulletTheme =
    parentColorScheme?.split(" ").join("-").toLowerCase() ||
    "transparent-dark-text"
  const themeValue =
    parentColorScheme?.split(" ").join("-").toLowerCase() ||
    colorScheme?.split(" ").join("-").toLowerCase() ||
    "transparent-dark-text"
  const theme = formatTheme({ theme: themeValue })

  const returnFormattedSpecialChar = (char, i) => {
    return (
      <React.Fragment key={i}>
        <sup>{char}</sup>
      </React.Fragment>
    )
  }

  const isExternalLink = url => {
    if (typeof window !== "undefined" && typeof document !== "undefined") {
      // create a temp anchor element
      const anchorEl = document.createElement("a")
      // set href value for temp anchor element
      anchorEl.href = url
      // return true if url doesn't match our homepage url
      return anchorEl.origin !== window.location.origin
    }
  }

  const isContentWithLineBreaks = content => {
    return content.toString().includes("\n") ? true : false
  }

  const options = {
    renderText: text => {
      let content
      if (!isContentWithLineBreaks(text)) {
        content = text.toString().split(/(™)|(©)|(®)/g)
        content.forEach((character, i) => {
          if (character?.trim() === "™") {
            content[i] = returnFormattedSpecialChar(<>&trade;</>, i)
          }
          if (character?.trim() === "©") {
            content[i] = returnFormattedSpecialChar(<>&copy;</>, i)
          }
          if (character?.trim() === "®") {
            content[i] = returnFormattedSpecialChar(<>&reg;</>, i)
          }
        })
      } else {
        content = text.toString().split("\n")
        return content.reduce((children, textContent, index) => {
          return [...children, index > 0 && <br key={index} />, textContent]
        }, [])
      }

      return content
    },
    renderMark: {
      [MARKS.BOLD]: text => <strong>{text}</strong>,
    },
    renderNode: {
      [INLINES.ASSET_HYPERLINK]: (node, children) => {
        const findAssetHyperLink = assetHyperLink?.length
          ? assetHyperLink.find(asset => {
              return (
                JSON.stringify(asset?.content) === JSON.stringify(node?.content)
              )
            })
          : {}
        if (findAssetHyperLink?.link?.file && findAssetHyperLink?.link?.text) {
          return (
            <a href={findAssetHyperLink?.link?.file} target="_blank">
              {findAssetHyperLink?.link?.text}
            </a>
          )
        }
      },
      [BLOCKS.PARAGRAPH]: (node, children) => <p>{children}</p>,
      [BLOCKS.EMBEDDED_ENTRY]: node => {
        if (node?.data?.target) {
          const {
            __typename: typename,
            ariaLabel,
            ariaRole,
            displayText,
            url,
            openInNewWindow,
            image,
          } = node?.data?.target
          if (typename === "ContentfulLinkWrapper") {
            if (openInNewWindow) {
              return (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  role={ariaRole || "link"}
                  aria-label={ariaLabel}
                >
                  {displayText}
                </a>
              )
            } else {
              return (
                <Link to={url} role={ariaRole || "link"} aria-label={ariaLabel}>
                  {displayText}
                </Link>
              )
            }
          } else if (typename === "ContentfulIcons") {
            const icon = formatIcons(node?.data?.target?.icon)
            return <Icon type={icon} size="32px" />
          } else if (typename === "ContentfulImageWithFocalPoint") {
            const imageSizes = {
              desktop: {
                desktop: {
                  width: 1280,
                  height: 720,
                  ratio: {
                    width: 16,
                    height: 9,
                  },
                },
                tablet: {
                  width: 960,
                  height: 480,
                  ratio: {
                    width: 2,
                    height: 1,
                  },
                },
                mobile: {
                  width: 600,
                  height: 467,
                  ratio: {
                    width: 9,
                    height: 7,
                  },
                },
              },
            }
            return (
              <div className="rich-text-image-container">
                <FocusedImage
                  focalPointImage={image?.file}
                  alt={image?.title}
                  fullHeightWrapper={false}
                  imageSizes={imageSizes}
                />
              </div>
            )
          } else if (typename === "ContentfulVideo") {
            return (
              <VideoCovered
                videoId={node?.data?.target?.videoUrl}
                image={node?.data?.target?.videoThumbnail?.image?.file}
              />
            )
          } else if (typename === "ContentfulImage") {
            const formattedImageComponent = {
              ...node?.data?.target,
              image: node?.data?.target?.embeddedImage,
            }
            return (
              <div
                className={`richtext-image-wrapper ${
                  formattedImageComponent?.caption
                    ? "richtext-image-wrapper--has-caption"
                    : ""
                }`}
              >
                <PageSections
                  pageSections={[formattedImageComponent]}
                  proseText={proseText}
                />
                {formattedImageComponent?.caption && (
                  <p className="richtext-image-caption">
                    {formattedImageComponent?.caption}
                  </p>
                )}
              </div>
            )
          } else if (typename === "ContentfulLayoutTestimonials") {
            return (
              <FormatAndRenderLayoutTestimonials
                data={{
                  ...node?.data?.target,
                  theme: "White",
                }}
                locale={node?.data?.target?.locale}
                currentSpaceId={node?.data?.target?.currentSpaceId}
                siteIdentifier={node?.data?.target?.siteIdentifier}
                homepageSlug={node?.data?.target?.homepageSlug}
              />
            )
          } else if (typename === "ContentfulActionBlock") {
            return (
              <FormatAndRenderActionBlock
                data={{ ...node?.data?.target }}
                locale={node?.data?.target?.locale}
                currentSpaceId={node?.data?.target?.currentSpaceId}
                siteIdentifier={node?.data?.target?.siteIdentifier}
                homepageSlug={node?.data?.target?.homepageSlug}
              />
            )
          }
          return <GeneralPageSections pageSections={[node?.data?.target]} />
        }
      },
      [INLINES.EMBEDDED_ENTRY]: node => {
        if (node?.data?.target) {
          const {
            __typename: typename,
            ariaLabel,
            ariaRole,
            displayText,
            url,
            openInNewWindow,
          } = node?.data?.target
          if (typename === "ContentfulLinkWrapper") {
            if (openInNewWindow) {
              return (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  role={ariaRole || "link"}
                  aria-label={ariaLabel}
                >
                  {displayText}
                </a>
              )
            } else {
              return (
                <Link to={url} role={ariaRole || "link"} aria-label={ariaLabel}>
                  {displayText}
                </Link>
              )
            }
          }
          return (
            <PageSections
              pageSections={[node?.data?.target]}
              proseText={proseText}
            />
          )
        }
      },
      [INLINES.HYPERLINK]: (node, children) => {
        if (isExternalLink(node?.data?.uri)) {
          return (
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={node?.data?.uri}
              className="text-link"
            >
              {children}
            </a>
          )
        } else {
          return (
            <Link to={node?.data?.uri} className="text-link">
              {children}
            </Link>
          )
        }
      },
    },
  }

  const checkForChildContent = () => {
    //If we have child content we need to apply the content class
    //so that we get the appropriate styles and spacing

    if (isChildContent) {
      let classes = includeClasses
        ? `richtext-container content ${themeValue}`
        : ""
      return (
        <div className="cta-container" style={{ color: "inherit" }}>
          <div className={classes} data-sb-field-path="content">
            {renderRichText(richText, options)}
          </div>
        </div>
      )
    } else {
      let classes = includeClasses
        ? `richtext-container ${themeValue} richtext-container__bullet-theme--${bulletTheme}`
        : ""
      return (
        <div
          data-sb-object-id={contentfulId ? contentfulId : null}
          className={classes}
          style={{ color: theme?.color, background: theme?.backgroundColor }}
        >
          {renderRichText(richText, options)}
        </div>
      )
    }
  }

  return richText ? checkForChildContent() : null
}

export default RichText
